import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  Field,
  formValueSelector,
  propTypes as formPropTypes,
} from 'redux-form'
import { lpForm } from 'lp-form'
import { PartnerAutoSuggestInput, ComboBox } from 'components'
import * as Types from 'types'
import { formatObjectsToIds, parseIdsToObjects, serializeOptions } from 'utils'
import { SubmitButton, ButtonArea } from 'lp-components'
import { withProps } from 'recompose'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'

const propTypes = {
  ...formPropTypes,
  artistOptions: PropTypes.arrayOf(Types.searchSuggestion),
  schoolPartnerships: PropTypes.array,
  programPartnerships: PropTypes.array,
  visibleSchoolYearId: PropTypes.string.isRequired,
  fetchArtist: PropTypes.func.isRequired,
  selectedArtist: PropTypes.object,
}

function ArtistPartnershipForm({
  handleSubmit,
  submitting,
  artistOptions,
  artistId,
  schoolPartnerships,
  programPartnerships,
  visibleSchoolYearId,
  fetchArtist,
  selectedArtist,
}) {
  const isArtistSelected = Boolean(artistId)

  useEffect(() => {
    if (visibleSchoolYearId && artistId) {
      fetchArtist(artistId, visibleSchoolYearId)
    }
  }, [visibleSchoolYearId, artistId, fetchArtist])

  const selectedArtistSchoolPartnerships =
    selectedArtist?.partnerships?.filter((p) => p.schoolId) || []

  const filteredSchoolPartnerships =
    schoolPartnerships.filter(
      (p) =>
        !selectedArtistSchoolPartnerships.some(
          (sp) => sp.schoolId === p.schoolId
        )
    ) || []

  return (
    <form onSubmit={handleSubmit}>
      <Field
        name="artistId"
        label="Enter a Teaching Artist Name"
        component={PartnerAutoSuggestInput}
        suggestions={artistOptions}
        parse={(option) => (option ? option.id : null)}
      />

      {isArtistSelected && (
        <>
          <Field
            name="programs"
            label="Choose the programs that are associated with this Teaching Artist"
            component={ComboBox}
            options={serializeOptions(programPartnerships, {
              key: 'name',
              value: 'id',
            })}
            isMulti={true}
            format={formatObjectsToIds('id')}
            parse={parseIdsToObjects('id')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
          />

          <Field
            name="schools"
            label="Choose the schools that are associated with this Teaching Artist"
            component={ComboBox}
            options={serializeOptions(filteredSchoolPartnerships, {
              key: 'schoolName',
              value: 'schoolId',
            })}
            isMulti={true}
            format={formatObjectsToIds('schoolId')}
            parse={parseIdsToObjects('schoolId')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
          />
        </>
      )}
      <ButtonArea>
        <SubmitButton {...{ submitting, style: 'secondary' }}>
          Add Teaching Artist
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

ArtistPartnershipForm.propTypes = propTypes

const selectFormValue = formValueSelector('artist-add-partnership')

function mapStateToProps(state) {
  return {
    artistId: selectFormValue(state, 'artistId'),
    selectedArtist: selectors.artist(state),
  }
}

const mapDispatchToProps = {
  fetchArtist: apiActions.fetchArtist,
}

export default compose(
  withProps(({ programPartnerships, artistOptions }) => {
    return {
      beforeSubmit: ({ artistId, programs, schools, ...values }) => {
        const fullArtist = artistOptions?.find(
          (artist) => artist.id === artistId
        )
        const fullPrograms = programs
          ? programs.map((program) => {
              const originalProgram = programPartnerships?.find(
                (p) => p.id === program.id
              )
              return originalProgram || program
            })
          : []

        return {
          ...values,
          artist: fullArtist,
          programs: fullPrograms,
          schoolIds: formatObjectsToIds('schoolId', schools),
        }
      },
    }
  }),
  lpForm({
    name: 'artist-add-partnership',
    constraints: {
      artistId: {
        presence: { message: '^Teaching Artist selection cannot be blank' },
      },
    },
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(ArtistPartnershipForm)
